/**
 * #21 (ESERV2-1429) — identify items present on one side but not the other.
 *
 * The Heritage pane of the link screen is split into "In your items" (the part
 * is linked, or the server matched it to a local inventory item) and "Not in
 * your items" (the company has no counterpart at all). The match verdict comes
 * from the server (`matched_item_id`, computed with the same tiers auto-link
 * uses) because the client's local list is search-filtered and capped — absence
 * from the visible list proves nothing.
 */

export type HeritageSectionLabel = "inYourItems" | "notInYourItems";

export type HeritageSectionEntry<T> =
	| { kind: "header"; label: HeritageSectionLabel; count: number }
	| { kind: "item"; item: T };

type MatchAnnotated = {
	heritage_part_number: string;
	matched_item_id?: number | null;
};

/**
 * A Heritage part counts as "in your items" when it is linked to a local item
 * (the caller supplies that — link state lives client-side) or when the server
 * matched it to one by part number.
 */
export function heritagePartHasLocalCounterpart<T extends MatchAnnotated>(
	item: T,
	isLinked: (item: T) => boolean
): boolean {
	return isLinked(item) || item.matched_item_id != null;
}

/**
 * Flatten the results into one render list with section headers interleaved,
 * so the pane can keep a single `.map` over one scroll container. Original
 * server order is preserved inside each section. A header renders only when
 * its section has items.
 */
export function buildHeritageSectionedList<T extends MatchAnnotated>(
	items: T[],
	isLinked: (item: T) => boolean
): Array<HeritageSectionEntry<T>> {
	const inYourItems: T[] = [];
	const notInYourItems: T[] = [];
	for (const item of items) {
		(heritagePartHasLocalCounterpart(item, isLinked) ? inYourItems : notInYourItems).push(item);
	}

	const entries: Array<HeritageSectionEntry<T>> = [];
	if (inYourItems.length > 0) {
		entries.push({ kind: "header", label: "inYourItems", count: inYourItems.length });
		for (const item of inYourItems) {
			entries.push({ kind: "item", item });
		}
	}
	if (notInYourItems.length > 0) {
		entries.push({ kind: "header", label: "notInYourItems", count: notInYourItems.length });
		for (const item of notInYourItems) {
			entries.push({ kind: "item", item });
		}
	}
	return entries;
}

type LocalAnnotated = {
	vendor_part_number: string | null;
	matched_heritage_part?: string | null;
};

/**
 * The mirror check for the local pane: true when the item is neither linked to
 * a Heritage part nor matched to anything in the downloaded catalog. Rows from
 * an older API response (no `matched_heritage_part` field) return false — never
 * claim "not in Heritage" without the server's verdict.
 */
export function localItemHasNoHeritageCounterpart(item: LocalAnnotated): boolean {
	if (!("matched_heritage_part" in item) || item.matched_heritage_part === undefined) {
		return false;
	}
	return !item.vendor_part_number && item.matched_heritage_part == null;
}
